import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  EmptyState,
  Flex,
  LoadingIndicator,
  QueryLoader,
  Text,
} from "@jsq/ds/core";
import { ActivityEventItem } from "./ActivityEventItem";
import { ActivityTypeFilter } from "./AuditLogFilters/ActivityTypeFilter";
import { DateFilter } from "./AuditLogFilters/DateFilter";
import { EntityFilter } from "./AuditLogFilters/EntityFilter";
import { clearFiltersWrap, filterRow } from "./AuditLogFilters/styles_vanilla.css";
import { PositionDetailModal } from "./PositionDetailModal";
import {
  cardHeader,
  cardWrapper,
  columnHeaderDate,
  columnHeaderMuted,
  columnHeaderRow,
  columnHeaderThird,
  emptyState,
  emptyStatePreview,
  headerSubtitle,
  headerTitleColumn,
  timelineBody,
  viewAllButton,
} from "./styles_vanilla.css";
import { Timeline } from "./Timeline";
import { buildActivityTimelineItems } from "./Timeline/helpers";
import { useComplianceAuditLog } from "./useComplianceAuditLog";
import type { ActivityEvent, AuditLogDateFilter, AuditLogFilters } from "./types";
import type { ComplianceCaseTypeEnum } from "../../../graphql/supergraphTypes";

export const AUDIT_LOG_SUMMARY_PREVIEW_LIMIT = 3;

const EMPTY_FILTERS: AuditLogFilters = {
  dateFilter: null,
  caseTypeFilters: null,
  entityIds: null,
};

type Props = {
  arenaId: number;
  arenaName?: string;
  entityId?: number;
  positionId?: number;
  maxVisibleItems?: number;
  onViewAll?: () => void;
};

export const ComplianceActivityCardBody: React.FC<Props> = ({
  arenaId,
  arenaName,
  entityId,
  positionId,
  maxVisibleItems,
  onViewAll,
}) => {
  const [filters, setFilters] = useState<AuditLogFilters>(EMPTY_FILTERS);

  const isArenaView = entityId == null && positionId == null;
  const isPositionView = positionId != null;
  const isPreview = maxVisibleItems != null;

  const entityIds = useMemo(() => {
    if (entityId != null) return [entityId];
    return filters.entityIds ?? undefined;
  }, [entityId, filters.entityIds]);

  const { events, hasMore, isLoadingMore, loadMore, queryResult } = useComplianceAuditLog({
    arenaId,
    arenaName,
    entityIds,
    positionId,
    caseTypes: filters.caseTypeFilters ?? undefined,
    dateFrom: filters.dateFilter?.dateFrom,
    dateTo: filters.dateFilter?.dateTo,
    limit: maxVisibleItems ?? 10,
  });

  const [modalEvent, setModalEvent] = useState<ActivityEvent | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasEvents = events.length > 0;

  useEffect(() => {
    if (isPreview) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isPreview, hasMore, isLoadingMore, loadMore, hasEvents]);

  const filtersActive =
    filters.dateFilter != null || filters.caseTypeFilters != null || filters.entityIds != null;

  const clearFilters = useCallback(() => setFilters(EMPTY_FILTERS), []);

  const handleDateChange = useCallback(
    (dateFilter: AuditLogDateFilter | null) => setFilters((f) => ({ ...f, dateFilter })),
    [],
  );

  const handleCaseTypeChange = useCallback(
    (caseTypeFilters: ComplianceCaseTypeEnum[] | null) =>
      setFilters((f) => ({ ...f, caseTypeFilters })),
    [],
  );

  const handleEntityChange = useCallback(
    (entityIds: number[] | null) => setFilters((f) => ({ ...f, entityIds })),
    [],
  );

  const displayedEvents = useMemo(
    () => (isPreview ? events.slice(0, maxVisibleItems) : events),
    [events, isPreview, maxVisibleItems],
  );

  const showViewAll = isPreview && !!onViewAll && events.length > 0;

  const timelineItems = useMemo(
    () =>
      buildActivityTimelineItems(displayedEvents, (event) => (
        <ActivityEventItem
          event={event}
          onPositionClick={() => setModalEvent(event)}
          showPositionLine={!isPositionView}
        />
      )),
    [displayedEvents, isPositionView],
  );

  return (
    <>
      <Flex className={cardHeader} alignItems="flex-start" justifyContent="space-between">
        <Flex flexDirection="column" className={headerTitleColumn}>
          <Text variant="h3">Compliance activity</Text>
          <Text variant="regular-md" className={headerSubtitle}>
            {isPreview ? "Recent compliance activity" : "All compliance activity"}
          </Text>
        </Flex>
        {showViewAll && (
          <Button displayAsLink isInline onClick={onViewAll} className={viewAllButton}>
            View all
          </Button>
        )}
      </Flex>

      <QueryLoader
        result={[{ ...queryResult, loading: queryResult.loading && !isLoadingMore }]}
      >
        {isPreview ? (
          <>
            {displayedEvents.length === 0 ? (
              <div className={emptyStatePreview}>
                <EmptyState
                  title="No recent activity"
                  description="Compliance activity will appear here as it happens."
                />
              </div>
            ) : (
              <div className={timelineBody}>
                <Timeline items={timelineItems} showLabels={false} showDividers={false} />
              </div>
            )}
          </>
        ) : (
          <>
            <Flex className={filterRow} alignItems="flex-start">
              <DateFilter value={filters.dateFilter} onChange={handleDateChange} />
              {isArenaView && (
                <EntityFilter value={filters.entityIds} onChange={handleEntityChange} />
              )}
              <ActivityTypeFilter
                value={filters.caseTypeFilters}
                onChange={handleCaseTypeChange}
              />
              {filtersActive && (
                <Flex alignItems="center" className={clearFiltersWrap}>
                  <Button displayAsLink isInline onClick={clearFilters}>
                    Clear filters
                  </Button>
                </Flex>
              )}
            </Flex>
            <div className={columnHeaderRow}>
              <Text variant="strong-sm" className={columnHeaderDate}>
                Date
              </Text>
              <div />
              <Flex
                className={columnHeaderThird}
                justifyContent="space-between"
                alignItems="center"
              >
                <Text variant="strong-sm" className={columnHeaderMuted}>
                  Compliance activity
                </Text>
                <Text variant="strong-sm" className={columnHeaderMuted}>
                  Activity type
                </Text>
              </Flex>
            </div>
            {events.length === 0 ? (
              <div className={emptyState}>
                {filtersActive ? (
                  <EmptyState
                    illustration="searching"
                    title="No matching activity"
                    description="No activities match your filters. Try adjusting or clearing them."
                  />
                ) : (
                  <EmptyState
                    illustration="data"
                    title="No compliance activity yet"
                    description="Compliance activity will appear here as it happens."
                  />
                )}
              </div>
            ) : (
              <div className={timelineBody}>
                <Timeline items={timelineItems} showLabels />
                <div ref={sentinelRef} />
                {isLoadingMore && (
                  <Flex justifyContent="center" style={{ padding: "16px 0" }}>
                    <LoadingIndicator size="small" variant="primary" />
                  </Flex>
                )}
              </div>
            )}
          </>
        )}

        <PositionDetailModal
          show={modalEvent != null}
          onHide={() => setModalEvent(null)}
          arenaId={arenaId}
          caseId={modalEvent?.id ?? 0}
          profileName={modalEvent?.profileName ?? ""}
        />
      </QueryLoader>
    </>
  );
};

export const ComplianceActivityCard: React.FC<Props> = ({
  arenaId,
  arenaName,
  entityId,
  positionId,
  maxVisibleItems,
  onViewAll,
}) => (
  <Card className={cardWrapper}>
    <ComplianceActivityCardBody
      arenaId={arenaId}
      arenaName={arenaName}
      entityId={entityId}
      positionId={positionId}
      maxVisibleItems={maxVisibleItems}
      onViewAll={onViewAll}
    />
  </Card>
);
